// Maps a schema.org Recipe JSON-LD node onto this app's recipe shape (see
// Schemas/recipe-schema.md). The output here is intentionally loose --
// final validation/defaulting/enum-filtering happens client-side via the
// existing normalizeImportedRecipe() (src/lib/recipeUtils.js), which this
// deliberately does not duplicate.
//
// Kept in sync by hand with src/lib/constants.ts (UNITS/NON_NUMERIC_UNITS)
// -- not imported directly since this runs as a separate Deno deployment,
// not bundled by Vite.
import { CATEGORY_LOOKUP } from "./categoryLookup.ts";
import { normalizeIngredientName } from "./itemIdentity.ts";

const UNITS = ["", "g", "kg", "ml", "l", "tsp", "tbsp", "cup", "oz", "lb", "clove", "can", "pinch", "piece(s)", "quart", "gallon", "packet", "dash"];
const NON_NUMERIC_UNITS = ["to taste", "as needed", "splash"];

// Grocery category for an imported ingredient, from a table learned from the
// household's own recipes (categoryLookup.ts, generated by
// scripts/build-category-lookup.mjs). An exact match on the normalised name
// only -- no guessing from partial words -- and "other" when there is none,
// which is what every imported ingredient used to get.
export function categoryForIngredient(name: string): string {
  return CATEGORY_LOOKUP[normalizeIngredientName(name)] ?? "other";
}

// Recipe sites commonly use the Unicode vulgar-fraction glyph instead of
// ASCII "1/2" (e.g. "1½ cups"). Mapped to the same "N/D" string format the
// rest of the app already expects for `quantity` (see
// Schemas/recipe-schema.md), not a decimal.
const UNICODE_FRACTIONS: Record<string, string> = {
  "¼": "1/4", "½": "1/2", "¾": "3/4",
  "⅓": "1/3", "⅔": "2/3",
  "⅕": "1/5", "⅖": "2/5", "⅗": "3/5", "⅘": "4/5",
  "⅙": "1/6", "⅚": "5/6",
  "⅛": "1/8", "⅜": "3/8", "⅝": "5/8", "⅞": "7/8",
};
const FRACTION_CHAR_CLASS = Object.keys(UNICODE_FRACTIONS).join("");

// "to taste"/"as needed" trail the ingredient in real text ("Salt, to
// taste" or "Salt to taste") rather than lead it -- checked against the
// tail of the name/prep split, not the start of the line. "splash" is the
// odd one out (almost always leads, "a splash of dry sherry"), handled
// separately in parseIngredientLine.
const TRAILING_NON_NUMERIC_UNITS = NON_NUMERIC_UNITS.filter((u) => u !== "splash");
const LEADING_SPLASH_PATTERN = /^a?\s*splash\s+of\s+/i;

const UNIT_SYNONYMS: Record<string, string> = {
  cups: "cup", c: "cup", "c.": "cup",
  tablespoons: "tbsp", tablespoon: "tbsp", tbsp: "tbsp", "tbsp.": "tbsp", tbs: "tbsp", tbsps: "tbsp", "tbsps.": "tbsp",
  teaspoons: "tsp", teaspoon: "tsp", tsp: "tsp", "tsp.": "tsp", tsps: "tsp", "tsps.": "tsp",
  pounds: "lb", pound: "lb", lbs: "lb", "lb.": "lb", "lbs.": "lb",
  ounces: "oz", ounce: "oz", "oz.": "oz",
  grams: "g", gram: "g",
  kilograms: "kg", kilogram: "kg",
  milliliters: "ml", milliliter: "ml",
  liters: "l", liter: "l", litre: "l", litres: "l",
  cloves: "clove",
  cans: "can",
  quarts: "quart",
  gallons: "gallon",
  packets: "packet",
  pinches: "pinch",
  dashes: "dash",
  pieces: "piece(s)", piece: "piece(s)",
};

function firstOrJoin(value: unknown): string {
  if (Array.isArray(value)) return value.filter((v) => typeof v === "string").join(", ");
  return typeof value === "string" ? value : "";
}

// ISO 8601 duration ("PT1H30M") -> whole minutes. Returns null on anything
// unparseable so callers can fall back to a schema default.
function parseIsoDurationMinutes(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const match = /^P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/i.exec(value.trim());
  if (!match) return null;
  const [, days, hours, minutes, seconds] = match;
  if (!days && !hours && !minutes && !seconds) return null;
  const total =
    (Number(days) || 0) * 24 * 60 +
    (Number(hours) || 0) * 60 +
    (Number(minutes) || 0) +
    Math.round((Number(seconds) || 0) / 60);
  return total > 0 ? total : null;
}

function parseYield(raw: unknown): { servings: number | null; yieldText: string } {
  const text = firstOrJoin(raw).trim();
  if (!text) return { servings: null, yieldText: "" };
  const match = /^(\d+)/.exec(text);
  if (match && `${Number(match[1])}` === match[1]) {
    // A bare number ("4") -- treat as servings. Anything with extra words
    // ("4 servings", "makes 4 dozen") is more useful kept as free-text yield.
    if (match[0] === text) return { servings: Number(match[1]), yieldText: "" };
  }
  return { servings: null, yieldText: text };
}

// Converts a matched quantity token into the app's "N/D"-style string
// format -- handles a bare Unicode fraction glyph ("½") and a whole number
// directly followed by one ("1½"); everything else (plain digits, ASCII
// "1/2" or "1 1/2") already matches that format as-is.
function normalizeQuantityToken(raw: string): string {
  const wholeAndFraction = new RegExp(`^(\\d+)\\s*([${FRACTION_CHAR_CLASS}])$`).exec(raw);
  if (wholeAndFraction) return `${wholeAndFraction[1]} ${UNICODE_FRACTIONS[wholeAndFraction[2]]}`;
  if (UNICODE_FRACTIONS[raw]) return UNICODE_FRACTIONS[raw];
  return raw;
}

// Best-effort split of a free-text ingredient line ("1½ cups flour,
// sifted") into the app's structured shape. Anything this can't confidently
// parse folds into `name` -- the user reviews/edits in the preview step
// either way, so approximate is fine here.
export function parseIngredientLine(line: string): { quantity: string; unit: string; name: string; prep: string } {
  let rest = line.trim();

  let quantity = "";
  // Ordered so the more specific alternatives (a whole number glued to a
  // fraction, an ASCII mixed fraction) are tried before the plain "\d+"
  // fallback -- regex alternation takes the first alternative that
  // matches at this position, not the longest, so a bare \d+ earlier in
  // the list would match just the "1" in "1½" and strand the "½".
  const qtyMatch = new RegExp(
    `^(\\d+\\s+\\d+\\/\\d+|\\d+\\s*[${FRACTION_CHAR_CLASS}]|\\d+\\/\\d+|[${FRACTION_CHAR_CLASS}]|\\d+(?:\\.\\d+)?)\\s*`
  ).exec(rest);
  if (qtyMatch) {
    quantity = normalizeQuantityToken(qtyMatch[1]);
    rest = rest.slice(qtyMatch[0].length);
  }

  let unit = "";

  const splashMatch = LEADING_SPLASH_PATTERN.exec(rest);
  if (splashMatch) {
    unit = "splash";
    rest = rest.slice(splashMatch[0].length);
  }

  if (!unit) {
    const unitMatch = /^([a-zA-Z.]+)\s+/.exec(rest);
    if (unitMatch) {
      const rawStripped = unitMatch[1].replace(/\.$/, "");
      // Cookbook shorthand for tablespoon/teaspoon is often a single letter
      // that differs only by case ("T butter" vs "t salt") -- must be
      // checked against the original case before the general lookup below
      // lowercases everything and the distinction is lost.
      let canonical = "";
      if (rawStripped === "T") canonical = "tbsp";
      else if (rawStripped === "t") canonical = "tsp";
      else {
        const token = unitMatch[1].toLowerCase().replace(/\.$/, "");
        canonical = UNIT_SYNONYMS[unitMatch[1].toLowerCase()] || (UNITS.includes(token) ? token : "");
      }
      if (canonical) {
        unit = canonical;
        rest = rest.slice(unitMatch[0].length);
      }
    }
  }

  let name = rest;
  let prep = "";
  const commaIndex = rest.indexOf(",");
  if (commaIndex !== -1) {
    name = rest.slice(0, commaIndex).trim();
    prep = rest.slice(commaIndex + 1).trim();
  }

  // "to taste"/"as needed" trail the ingredient rather than lead it, so
  // check the tail of whichever piece holds it now: `prep` if a comma
  // split one out ("salt, to taste"), otherwise the end of `name` itself
  // ("salt to taste", no comma).
  if (!unit) {
    for (const trailing of TRAILING_NON_NUMERIC_UNITS) {
      if (prep && prep.replace(/\.$/, "").trim().toLowerCase() === trailing) {
        unit = trailing;
        prep = "";
        break;
      }
      if (!prep) {
        const tailPattern = new RegExp(`,?\\s*${trailing.replace(/\s+/g, "\\s+")}\\.?\\s*$`, "i");
        if (tailPattern.test(name)) {
          unit = trailing;
          name = name.replace(tailPattern, "").trim();
          break;
        }
      }
    }
  }

  return { quantity: quantity || (unit && NON_NUMERIC_UNITS.includes(unit) ? "" : "1"), unit, name: name.trim(), prep };
}

const AND_SPLIT_PATTERN = /\s+and\s+/i;
const TRAILING_SUFFIX_PATTERN = new RegExp(
  `,?\\s*(${TRAILING_NON_NUMERIC_UNITS.join("|").replace(/\s+/g, "\\s+")})\\.?\\s*$`,
  "i"
);

// A bare "and" list with no quantity at all ("Salt and pepper", "kosher
// salt and freshly ground black pepper, to taste") is common shorthand for
// two separate ingredients bundled onto one line. Only split when the whole
// line has no digits -- a real quantity ("1 cup flour and cornmeal") makes
// the split ambiguous, so that's left as one line for manual review instead.
// A shared trailing "to taste"/"as needed" is pulled off first and
// reapplied to every part, so it isn't lost off all but the last one.
export function splitAndList(line: string): string[] {
  if (/\d/.test(line) || !AND_SPLIT_PATTERN.test(line)) return [line];

  let base = line;
  let suffix = "";
  const suffixMatch = TRAILING_SUFFIX_PATTERN.exec(base);
  if (suffixMatch) {
    suffix = `, ${suffixMatch[1]}`;
    base = base.slice(0, suffixMatch.index).trim();
  }

  const parts = base.split(AND_SPLIT_PATTERN).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return [line];
  return parts.map((p) => `${p}${suffix}`);
}

function mapIngredients(raw: unknown): Array<{ component: string; name: string; prep: string; quantity: string; unit: string; category: string }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((line): line is string => typeof line === "string" && line.trim().length > 0)
    .flatMap((line) => splitAndList(line.trim()))
    .map((line) => {
      const parsed = parseIngredientLine(line);
      return { component: "", ...parsed, category: categoryForIngredient(parsed.name) };
    })
    .filter((ing) => ing.name);
}

type MappedInstruction = { component: string; type: "step" | "note"; text: string };

// "Do ahead: ..." / "Make-ahead: ..." is advice about timing, not a step to
// number, so it comes in as a note -- as does everything in a section with
// that name (feedback #42).
const DO_AHEAD = /^(do|make)[\s-]ahead\b/i;
const instruction = (component: string, text: string, forceNote = false): MappedInstruction => ({
  component,
  type: forceNote || DO_AHEAD.test(text) ? "note" : "step",
  text,
});

// Handles all three schema.org shapes: a plain string, an array of
// strings/HowToStep objects, or an array of HowToSection objects (each with
// a `name` and `itemListElement` of HowToSteps) -- the last of which maps
// naturally onto this app's ingredient/instruction "component" grouping.
function mapInstructions(raw: unknown): MappedInstruction[] {
  if (typeof raw === "string") {
    return raw
      .split(/\r?\n+/)
      .map((text) => text.trim())
      .filter(Boolean)
      .map((text) => instruction("", text));
  }
  if (!Array.isArray(raw)) return [];

  const out: MappedInstruction[] = [];
  for (const entry of raw) {
    if (typeof entry === "string") {
      if (entry.trim()) out.push(instruction("", entry.trim()));
      continue;
    }
    if (!entry || typeof entry !== "object") continue;
    const obj = entry as Record<string, unknown>;
    const types = Array.isArray(obj["@type"]) ? obj["@type"] : [obj["@type"]];

    if (types.includes("HowToSection")) {
      const sectionName = typeof obj.name === "string" ? obj.name.trim() : "";
      const doAheadSection = DO_AHEAD.test(sectionName);
      const items = Array.isArray(obj.itemListElement) ? obj.itemListElement : [];
      for (const item of items) {
        if (typeof item === "string") {
          if (item.trim()) out.push(instruction(sectionName, item.trim(), doAheadSection));
        } else if (item && typeof item === "object" && typeof (item as Record<string, unknown>).text === "string") {
          const text = ((item as Record<string, unknown>).text as string).trim();
          if (text) out.push(instruction(sectionName, text, doAheadSection));
        }
      }
      continue;
    }

    // HowToStep (or anything close enough to it) -- schema.org's `text`
    // field is the step content.
    const text = typeof obj.text === "string" ? obj.text.trim() : "";
    if (text) out.push(instruction("", text));
  }
  return out;
}

function parseNutritionNumber(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw !== "string") return 0;
  const match = /(\d+(?:\.\d+)?)/.exec(raw);
  return match ? Number(match[1]) : 0;
}

export function mapRecipeToAppSchema(recipe: Record<string, unknown>, sourceUrl: string) {
  const { servings, yieldText } = parseYield(recipe.recipeYield);
  const nutrition = (recipe.nutrition && typeof recipe.nutrition === "object" ? recipe.nutrition : {}) as Record<string, unknown>;

  const prepMinutes = parseIsoDurationMinutes(recipe.prepTime);
  const cookMinutes = parseIsoDurationMinutes(recipe.cookTime);
  const totalMinutes = parseIsoDurationMinutes(recipe.totalTime);

  return {
    title: firstOrJoin(recipe.name).trim(),
    cuisine: firstOrJoin(recipe.recipeCuisine).trim(),
    source: sourceUrl,
    servings: servings ?? undefined,
    yield: yieldText,
    // If only totalTime is given (no prep/cook split), it lands entirely in
    // cookTime -- a lossy but reasonable best-effort split, left for the
    // user to correct in the review step.
    activeTime: prepMinutes ?? undefined,
    cookTime: cookMinutes ?? (prepMinutes == null ? totalMinutes ?? undefined : undefined),
    caloriesPerServing: nutrition.calories !== undefined ? parseNutritionNumber(nutrition.calories) : undefined,
    proteinPerServing: nutrition.proteinContent !== undefined ? parseNutritionNumber(nutrition.proteinContent) : undefined,
    carbsPerServing: nutrition.carbohydrateContent !== undefined ? parseNutritionNumber(nutrition.carbohydrateContent) : undefined,
    fatPerServing: nutrition.fatContent !== undefined ? parseNutritionNumber(nutrition.fatContent) : undefined,
    ingredients: mapIngredients(recipe.recipeIngredient),
    instructions: mapInstructions(recipe.recipeInstructions),
  };
}
